import React, { useRef, useEffect, useState } from "react";

const playIcon = (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="white" />
    <path d="M15 29L29 20L15 11V29ZM20 40C17.2333 40 14.6333 39.475 12.2 38.425C9.76667 37.375 7.65 35.95 5.85 34.15C4.05 32.35 2.625 30.2333 1.575 27.8C0.525 25.3667 0 22.7667 0 20C0 17.2333 0.525 14.6333 1.575 12.2C2.625 9.76667 4.05 7.65 5.85 5.85C7.65 4.05 9.76667 2.625 12.2 1.575C14.6333 0.525 17.2333 0 20 0C22.7667 0 25.3667 0.525 27.8 1.575C30.2333 2.625 32.35 4.05 34.15 5.85C35.95 7.65 37.375 9.76667 38.425 12.2C39.475 14.6333 40 17.2333 40 20C40 22.7667 39.475 25.3667 38.425 27.8C37.375 30.2333 35.95 32.35 34.15 34.15C32.35 35.95 30.2333 37.375 27.8 38.425C25.3667 39.475 22.7667 40 20 40Z" fill="#CE2032" />
  </svg>
);

const pauseIcon = (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="white" />
    <path d="M14 28H18V12H14V28ZM22 28H26V12H22V28ZM20 40C17.2333 40 14.6333 39.475 12.2 38.425C9.76667 37.375 7.65 35.95 5.85 34.15C4.05 32.35 2.625 30.2333 1.575 27.8C0.525 25.3667 0 22.7667 0 20C0 17.2333 0.525 14.6333 1.575 12.2C2.625 9.76667 4.05 7.65 5.85 5.85C7.65 4.05 9.76667 2.625 12.2 1.575C14.6333 0.525 17.2333 0 20 0C22.7667 0 25.3667 0.525 27.8 1.575C30.2333 2.625 32.35 4.05 34.15 5.85C35.95 7.65 37.375 9.76667 38.425 12.2C39.475 14.6333 40 17.2333 40 20C40 22.7667 39.475 25.3667 38.425 27.8C37.375 30.2333 35.95 32.35 34.15 34.15C32.35 35.95 30.2333 37.375 27.8 38.425C25.3667 39.475 22.7667 40 20 40Z" fill="#CE2032" />
  </svg>
);

const PlayerControls = ({ src }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(() => 
    JSON.parse(localStorage.getItem("isPlaying") ?? "false")
  );
  const [slider, setSlider] = useState(0); 
  const [duration, setDuration] = useState(0);
  
  const rawAudioInfo = localStorage.getItem("audioInfo");
  const audioInfo = rawAudioInfo ? JSON.parse(rawAudioInfo) : {};

  // 1. AUTOPLAY ON SOURCE CHANGE
  useEffect(() => {
    if (audioRef.current && src) {
      // Force reload and reset slider for new audio track
      audioRef.current.load();
      setSlider(0);
      localStorage.setItem("currentTime", "0");

      // Attempt to play immediately
      const playPromise = audioRef.current.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            localStorage.setItem("isPlaying", "true");
          })
          .catch((err) => {
            console.warn("Autoplay was prevented by browser (waiting for user interaction):", err);
            setIsPlaying(false);
            localStorage.setItem("isPlaying", "false");
          });
      }

      localStorage.removeItem("audioEnded");
      window.dispatchEvent(new Event("storage"));
    }
  }, [src]);

  // 2. MANUAL PLAY/PAUSE SYNC
  useEffect(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  // 3. CROSS-TAB/COMPONENT STORAGE SYNC
  useEffect(() => {
    const handleSync = () => {
      const playingFromStorage = JSON.parse(localStorage.getItem("isPlaying") ?? "false");
      if (playingFromStorage !== isPlaying) {
        setIsPlaying(playingFromStorage);
      }
    };
    window.addEventListener("storage", handleSync);
    return () => window.removeEventListener("storage", handleSync);
  }, [isPlaying]);

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return "00:00";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds) % 60;
    const formattedMins = String(mins).padStart(2, "0");
    const formattedSecs = String(secs).padStart(2, "0");
    return hrs > 0 ? `${String(hrs).padStart(2, "0")}:${formattedMins}:${formattedSecs}` : `${formattedMins}:${formattedSecs}`;
  };

  const togglePlayPause = () => {
    const nextState = !isPlaying;
    setIsPlaying(nextState);
    localStorage.setItem("isPlaying", JSON.stringify(nextState));
  };

  const handleSliderChange = (event) => {
    const newSlider = parseFloat(event.target.value);
    setSlider(newSlider);
    if (audioRef.current) {
      audioRef.current.currentTime = newSlider;
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const time = audioRef.current.currentTime;
      setSlider(time);
      localStorage.setItem("currentTime", `${time}`);
    }
  };

  const handleAudioEnd = () => {
    localStorage.setItem("audioEnded", "true");
    setIsPlaying(false);
    localStorage.setItem("isPlaying", "false");
    window.dispatchEvent(new Event("storage"));
  };

  const updateDuration = () => {
    if (audioRef.current) {
      const dur = audioRef.current.duration;
      if (!isNaN(dur)) setDuration(dur);
    }
  };

  return (
    <div className="audio-player__controls">
      {/* Key={src} is critical here: it tells React to treat this as a 
        completely fresh element whenever the source changes. 
      */}
      <audio
        ref={audioRef}
        key={src} 
        onEnded={handleAudioEnd}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={updateDuration}
      >
        <source src={src} type="audio/mpeg" />
      </audio>
      
      <div>
        <button onClick={togglePlayPause} type="button" aria-label={isPlaying ? "Pausar" : "Reanudar"}>
          {isPlaying ? pauseIcon : playIcon}
        </button>
      </div>

      {!audioInfo.live && (
        <div className="audio-player__controls__slider">
          <span>{formatDuration(slider)}</span>
          <input
            aria-label="Progress bar"
            type="range"
            min="0"
            max={duration || 0}
            step="0.1"
            value={slider}
            onChange={handleSliderChange}
          />
          <span className="d-none d-xl-block">{formatDuration(duration)}</span>
        </div>
      )}
    </div>
  );
};

export default PlayerControls;